import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Info, WifiOff } from 'lucide-react';
import styles from './Banner.module.css';

export type BannerTone = 'info' | 'positive' | 'attention' | 'negative';

export interface BannerProps {
  tone?: BannerTone;
  /** Icon name for the non-colour marker; tone alone is never the signal. */
  icon?: 'info' | 'warning' | 'positive' | 'clock' | 'offline';
  title: string;
  children?: ReactNode;
  /** One clear action (§4.1 Banner). */
  action?: ReactNode;
}

/** Persistent banner with text, an icon shape and one clear action. */
export function Banner({ tone = 'info', icon, title, children, action }: BannerProps) {
  // Without an explicit icon the shape follows the tone, so an info note never shows a warning.
  const shape = icon ?? (tone === 'info' ? 'info' : tone === 'positive' ? 'positive' : 'warning');
  const Icon =
    shape === 'info'
      ? Info
      : shape === 'positive'
        ? CheckCircle2
        : shape === 'clock'
          ? Clock
          : shape === 'offline'
            ? WifiOff
            : AlertTriangle;
  return (
    <div className={styles.banner} data-tone={tone} role="note">
      <Icon size={18} aria-hidden="true" className={styles.icon} />
      <div className={styles.body}>
        <p className={styles.title}>{title}</p>
        {children}
      </div>
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}
